import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import * as api from '../api/client'
import { ViewDialog } from '../pages/settings/ViewBuilderPage'
import { Eye, EyeOff, Trash2, Plus, Users, RefreshCw, Pencil } from 'lucide-react'

// D-VIEWADMIN-1 — SaaS-admin management of a pharmacy's SAVED views. WYSIWYG: reads the
// pharmacy's live views (same pharmacy_views rows the portal uses) and writes back through the
// admin-scoped endpoints (?pharmacy_id). Add / hide / unhide / delete + per-user assignment.
// Access layering: assignment decides WHICH views a user sees; field RBAC (unchanged) still
// governs which columns within a view their role may see.
const SYSTEM_TAB_LABELS: Record<string, string> = {
  intake: 'Intake', insurance: 'Awaiting insurance', production: 'Production',
  ready: 'Ready & out', hold: 'On hold', refills: 'Refills', all: 'All prescriptions',
}
// D-TABCOLS-1: only the 5 StationQueue-based tabs share the Rx column list and can carry a per-tab
// column override. Insurance and Refills render bespoke layouts (claims / refill requests), so they
// have no shared columns to override.
const COLUMN_OVERRIDE_TABS = new Set(['intake', 'production', 'ready', 'hold', 'all'])

export default function ViewManagerPanel({ pharmacyId }: { pharmacyId?: string }) {
  const [sp, setSp] = useSearchParams()
  const [views, setViews] = useState<api.PharmacyView[]>([])
  const [systemTabs, setSystemTabs] = useState<api.SystemTabAccess[]>([])
  const [users, setUsers] = useState<{ id: string; email?: string; display_name?: string }[]>([])
  const [catalog, setCatalog] = useState<api.ViewFieldCatalogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [assignFor, setAssignFor] = useState<string | null>(null)
  const [assignSel, setAssignSel] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<{ id: string | null } | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!pharmacyId) return
    setLoading(true); setErr(null)
    try {
      // D3: always re-read the pharmacy's live view state before editing.
      const [v, u, st] = await Promise.all([
        api.listViews(pharmacyId),
        api.listPharmacyUsers(pharmacyId).catch(() => ({ users: [] as any[] })),
        api.getSystemTabsAccess(pharmacyId).catch(() => ({ tabs: [] as api.SystemTabAccess[] })),
      ])
      setViews(v.items ?? [])
      setSystemTabs(st.tabs ?? [])
      setUsers(((u as any).users ?? []).map((x: any) => ({ id: x.id ?? x.account_id, email: x.email, display_name: x.display_name })))
    } catch (e: any) { setErr(e?.message ?? 'Failed to load views') }
    finally { setLoading(false) }
  }, [pharmacyId])

  useEffect(() => { void load() }, [load])
  // RCA-CV-5 L1: the catalog is PER PHARMACY for an unbound admin — without the id the picker
  // showed no "Custom mappings" / "Custom fields" and no module-gated columns, so a view's
  // existing mapped columns were invisible here and could not be added. Refetch on selection.
  useEffect(() => {
    if (!pharmacyId) { setCatalog([]); return }
    api.getViewCatalog(pharmacyId).then(d => setCatalog(d.items ?? []))
      .catch(e => setErr(e?.message ?? 'Failed to load the field catalog'))
  }, [pharmacyId])

  const toggleHidden = async (v: api.PharmacyView) => {
    setBusy(v.id); setErr(null)
    try { await api.setViewHidden(v.id, !v.hidden, pharmacyId); await load() }
    catch (e: any) { setErr(e?.message ?? 'Hide/unhide failed') } finally { setBusy(null) }
  }
  const del = async (v: api.PharmacyView) => {
    if (!confirm(`Delete view "${v.name}"? This is permanent — hide it instead if you might want it back.`)) return
    setBusy(v.id); setErr(null)
    try { await api.deleteView(v.id, pharmacyId); await load() }
    catch (e: any) { setErr(e?.message ?? 'Delete failed') } finally { setBusy(null) }
  }
  const add = () => setEditing({ id: null })   // full create dialog: name + columns + rules + access
  const openAssign = async (v: api.PharmacyView) => {
    if (assignFor === v.id) { setAssignFor(null); return }
    setAssignFor(v.id); setAssignSel(new Set())
    try { const d = await api.getView(v.id, pharmacyId); setAssignSel(new Set((d.assignments ?? []).map(a => a.account_id))) } catch { /* start empty */ }
  }
  const saveAssign = async (v: api.PharmacyView) => {
    setBusy(v.id); setErr(null)
    try { await api.updateViewAssignments(v.id, [...assignSel], pharmacyId); setAssignFor(null); await load() }
    catch (e: any) { setErr(e?.message ?? 'Assign failed') } finally { setBusy(null) }
  }
  const openTabAssign = (t: api.SystemTabAccess) => {
    const key = `sys:${t.tab_key}`
    if (assignFor === key) { setAssignFor(null); return }
    setAssignFor(key); setAssignSel(new Set(t.assigned))   // current is already loaded on the tab
  }
  const saveTabAssign = async (t: api.SystemTabAccess) => {
    setBusy(`sys:${t.tab_key}`); setErr(null)
    try { await api.setSystemTabAccess(t.tab_key, [...assignSel], pharmacyId); setAssignFor(null); await load() }
    catch (e: any) { setErr(e?.message ?? 'Save failed') } finally { setBusy(null) }
  }
  const toggleTabHidden = async (t: api.SystemTabAccess) => {
    setBusy(`sys:${t.tab_key}`); setErr(null)
    try { await api.setSystemTabHidden(t.tab_key, !t.hidden, pharmacyId); await load() }
    catch (e: any) { setErr(e?.message ?? 'Hide/unhide failed') } finally { setBusy(null) }
  }

  if (!pharmacyId) return null

  const box: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 9, padding: 14, background: 'var(--color-surface)', marginBottom: 16 }
  const rowBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--color-border-subtle, #eef1f4)', borderRadius: 7, marginBottom: 6 }
  const badge: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'var(--color-surface-2, #f1f4f7)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }
  const actBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer' }
  const subHead: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', margin: '12px 0 6px' }

  // Shared user checklist used by both a system tab and a saved view (same access model).
  const userChecklist = (busyKey: string, onSave: () => void, note: string) => (
    <div style={{ border: '1px solid var(--color-border-subtle, #eef1f4)', borderTop: 'none', borderRadius: '0 0 7px 7px', padding: '8px 12px 12px', marginBottom: 8, background: 'var(--color-surface-2, #fafbfc)' }}>
      <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginBottom: 6 }}>{note}</div>
      {users.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No users found for this pharmacy.</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', maxHeight: 180, overflowY: 'auto' }}>
        {users.map(u => (
          <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, minWidth: 200 }}>
            <input type="checkbox" checked={assignSel.has(u.id)} onChange={() => setAssignSel(s => { const n = new Set(s); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n })} />
            {u.display_name || u.email || u.id}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={onSave} disabled={busy === busyKey} style={{ ...actBtn, fontWeight: 600 }}>Save access</button>
        <button onClick={() => setAssignFor(null)} style={actBtn}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <h2 style={{ fontSize: 14, margin: 0 }}>View &amp; tab access</h2>
        <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>Control who sees each built-in tab and saved view — and add, hide, or delete saved views.</span>
        <span style={{ flex: 1 }} />
        <button onClick={load} style={{ ...actBtn, border: 'none', background: 'transparent' }} title="Reload live state"><RefreshCw size={13} /></button>
        <button onClick={add} disabled={busy === '__new__'} style={{ ...actBtn, fontWeight: 600 }}><Plus size={13} /> New view</button>
      </div>

      {err && <div style={{ fontSize: 12, color: '#a23333', padding: '6px 0' }}>⚠ {err}</div>}
      {loading && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '6px 0' }}>Loading…</div>}

      <div style={subHead}>System tabs (built-in)</div>
      {systemTabs.map(t => {
        const key = `sys:${t.tab_key}`
        return (
          <div key={key}>
            <div style={{ ...rowBox, opacity: t.hidden ? 0.6 : 1, marginBottom: assignFor === key ? 0 : 6 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{SYSTEM_TAB_LABELS[t.tab_key] ?? t.tab_key}</span>
              {t.hidden && <span style={{ ...badge, background: '#fde8e8', color: '#a23333' }}>Hidden</span>}
              <span style={{ ...badge, background: 'transparent' }}>{t.hidden ? 'Off for everyone' : (t.rbac_enabled && t.assigned.length > 0 ? `${t.assigned.length} restricted` : 'All users')}</span>
              <span style={{ flex: 1 }} />
              {COLUMN_OVERRIDE_TABS.has(t.tab_key) && (
                <button onClick={() => { const next = new URLSearchParams(sp); next.set('listtab', t.tab_key); setSp(next) }} disabled={busy === key} style={actBtn} title="Give this tab its own columns (otherwise it follows the shared Rx columns)"><Pencil size={13} /> Columns</button>
              )}
              <button onClick={() => openTabAssign(t)} disabled={busy === key} style={actBtn}><Users size={13} /> Access</button>
              <button onClick={() => toggleTabHidden(t)} disabled={busy === key} style={actBtn}>{t.hidden ? <><Eye size={13} /> Unhide</> : <><EyeOff size={13} /> Hide</>}</button>
            </div>
            {assignFor === key && userChecklist(key, () => saveTabAssign(t),
              'No one checked → visible to all users. Check specific users → only they see this tab.')}
          </div>
        )
      })}

      <div style={subHead}>Saved views</div>
      {!loading && views.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '2px 0 6px' }}>No saved views for this pharmacy yet.</div>}
      {views.map(v => (
        <div key={v.id}>
          <div style={{ ...rowBox, opacity: v.hidden ? 0.6 : 1, marginBottom: assignFor === v.id ? 0 : 6 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{v.name}</span>
            {v.is_default && <span style={badge}>Default</span>}
            {v.hidden && <span style={{ ...badge, background: '#fde8e8', color: '#a23333' }}>Hidden</span>}
            <span style={{ ...badge, background: 'transparent' }}>{v.column_count} cols · {v.filter_count} filters</span>
            <span style={{ ...badge, background: 'transparent' }}>{v.assignment_count === 0 ? 'All users' : `${v.assignment_count} assigned`}</span>
            <span style={{ flex: 1 }} />
            <button onClick={() => setEditing({ id: v.id })} disabled={busy === v.id} style={actBtn}><Pencil size={13} /> Edit</button>
            <button onClick={() => openAssign(v)} disabled={busy === v.id} style={actBtn}><Users size={13} /> Access</button>
            <button onClick={() => toggleHidden(v)} disabled={busy === v.id} style={actBtn}>{v.hidden ? <><Eye size={13} /> Unhide</> : <><EyeOff size={13} /> Hide</>}</button>
            <button onClick={() => del(v)} disabled={busy === v.id} style={{ ...actBtn, color: '#a23333' }}><Trash2 size={13} /></button>
          </div>
          {assignFor === v.id && userChecklist(v.id, () => saveAssign(v),
            'No one checked → the view is visible to all users. Check specific users → only they see it. (Column-level PHI masking by role still applies inside the view.)')}
        </div>
      ))}

      {editing && (
        <ViewDialog catalog={catalog} editViewId={editing.id} pharmacyId={pharmacyId}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />
      )}
    </div>
  )
}
